import type { HtmlVNodeChildren, ReplaceResult } from '@nuxt-shared/lib/html-to-vnodes';
import type { ShortcodeMap, ShortcodeReplacer, ShortcodeToken } from './types';

export type * from './types';

/**
 * Допустимые символы параметра (после `_`): цифры, буквы, запятая, дефис,
 * точка. Ни решётки, ни пробелов — шорткод не должен «съедать» текст вокруг.
 */
const PARAMS_PATTERN = '[\\w.,-]+';

/** Пусто — только пробелы и неразрывные пробелы (hast отдаёт их символом). */
const BLANK_RE = /^[\s\u00a0]*$/;

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Числовая выборка элементов из параметра шорткода — общий синтаксис для всех
 * модулей: `2-5` — диапазон, `3,5` — перечисление, `1,3-5` — вместе.
 * Нумерация с единицы. `undefined` в ответе — «все элементы» (`#accordion#`).
 */
export function parseRangeSelector(params?: string): Set<number> | undefined {
  if (!params) return undefined;

  const selected = new Set<number>();

  for (const token of params.split(',')) {
    const [from, to] = token.split('-').map(Number);
    if (!from) continue;

    const last = to || from;
    for (let index = Math.min(from, last); index <= Math.max(from, last); index++) {
      selected.add(index);
    }
  }

  return selected;
}

/**
 * Регулярка на все имена сразу. Длинные имена идут первыми: иначе при
 * `image` + `imagegallery` альтернатива `image` съела бы `#imagegallery#`,
 * приняв `gallery` за параметры.
 */
function buildShortcodePattern(names: string[]): string {
  const alternatives = [...names]
    .sort((a, b) => b.length - a.length)
    .map(escapeRegExp)
    .join('|');

  return `#(${alternatives})(?:_(${PARAMS_PATTERN}))?#`;
}

/**
 * Режет текст на куски и шорткоды. `null` — знакомых шорткодов в тексте нет,
 * трогать его не надо. Незнакомые `#...#` остаются частью текстовых кусков.
 */
function tokenizeShortcodes(text: string, pattern: string, known: Set<string>) {
  // Свой экземпляр с `g` на каждый вызов: у общего сохраняется `lastIndex`
  // и совпадения находятся через раз.
  const matcher = new RegExp(pattern, 'gi');
  const tokens: ShortcodeToken[] = [];
  let cursor = 0;
  let match: RegExpExecArray | null;

  while ((match = matcher.exec(text)) !== null) {
    const name = match[1]?.toLowerCase();
    if (!name || !known.has(name)) continue;

    if (match.index > cursor) {
      tokens.push({ type: 'text', value: text.slice(cursor, match.index) });
    }
    tokens.push({ type: 'shortcode', name, params: match[2] });
    cursor = match.index + match[0].length;
  }

  if (!tokens.length) return null;

  if (cursor < text.length) tokens.push({ type: 'text', value: text.slice(cursor) });

  return tokens;
}

/**
 * Собирает из карты шорткодов один резак текстовых узлов.
 *
 * Все имена уходят в одну регулярку и разбираются за один проход, поэтому
 * новый модуль — это просто ещё один ключ карты: `{ accordion, gallery, image }`.
 *
 * `null` на пустой карте: потребителю не нужно ставить свои проверки.
 */
export function createShortcodeReplacer(
  shortcodes: ShortcodeMap,
): ShortcodeReplacer | null {
  const renderers = new Map(
    Object.entries(shortcodes || {}).map(([name, render]) => [
      name.toLowerCase(),
      render,
    ]),
  );

  if (!renderers.size) return null;

  const names = new Set(renderers.keys());
  const pattern = buildShortcodePattern([...names]);
  const tokenize = (text: string) => tokenizeShortcodes(text, pattern, names);

  return {
    replaceText(text: string): ReplaceResult {
      const tokens = tokenize(text);
      if (!tokens) return undefined;

      return tokens.reduce<HtmlVNodeChildren>((children, token) => {
        if (token.type === 'text') {
          children.push(token.value);
          return children;
        }

        const rendered = renderers.get(token.name)?.(token.params);
        if (rendered) children.push(...(Array.isArray(rendered) ? rendered : [rendered]));

        return children;
      }, []);
    },

    isShortcodeOnlyText(text: string): boolean {
      const tokens = tokenize(text);

      return (
        !!tokens &&
        tokens.every((token) => token.type !== 'text' || BLANK_RE.test(token.value))
      );
    },
  };
}
